// P3 vault bootstrap：网页端从零新建云端 vault（镜像 keytail-core vault.rs
// create_with_recovery + apply_cloud_wrap）。
//
// 流程（调用方先 login → registerDevice 拿 account/S_server → unlockAssist 取 sServerB64）：
//   设 PIN → 随机 VDK + salt + 恢复码 → KEK=HKDF(S_server, Argon2id(PIN,salt))
//   → wrapKey(KEK, VDK)（uses_server_secret=true）→ 默认分类/项目 + 空账号 → packVault。
//
// 决策 D：web bootstrap **省略 recovery_wrapped_vdk**（web 拿不到服务端 K_recovery）；
// 仍生成恢复码 + 存其指纹（供校验），但 SMS 重置需桌面端补登记一次（UI 提示）。

import {
  deriveKek,
  wrapKey,
  resealPayload,
  packVault,
  bytesToBase64,
  generateRecoveryCode,
  recoveryCodeFingerprint,
} from '../crypto'
import type { Category, Project } from './model'

export interface KdfParams {
  algorithm: string
  m_kib: number
  t_cost: number
  p_cost: number
  salt_b64: string
}

export interface NewCloudVault {
  /** 待 uploadVault(baseVersion=0) 的整包 blob（base64）。 */
  blobB64: string
  /** 明文恢复码（仅此一次展示，要求用户记录）。 */
  recoveryCode: string
}

/** 默认分类（对标 vault.rs default_categories，id/顺序一致以便跨端 parity）。 */
export function defaultCategories(now: string): Category[] {
  const defs: [string, string][] = [
    ['ai', 'AI 工具'],
    ['cloud', '云服务'],
    ['dev', '开发平台'],
    ['mail', '邮箱服务'],
    ['social', '社交平台'],
    ['pay', '支付与订阅'],
    ['work', '工作后台'],
    ['life', '个人生活'],
    ['mini', '杂项小站'],
  ]
  return defs.map(([id, name], i) => ({ id, name, icon: null, sort_order: i, created_at: now }))
}

/** 默认项目（对标 vault.rs default_projects）。 */
export function defaultProjects(now: string): Project[] {
  const defs: [string, string, string][] = [
    ['personal', '个人', '#3F6478'],
    ['work', '工作', '#B36A3E'],
  ]
  return defs.map(([id, name, color]) => ({ id, name, color, created_at: now }))
}

function randomBytes(n: number): Uint8Array {
  const b = new Uint8Array(n)
  crypto.getRandomValues(b)
  return b
}

function uuid(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return 'id_' + bytesToBase64(randomBytes(12)).replace(/[^a-zA-Z0-9]/g, '').slice(0, 16)
  }
}

export interface BuildOptions {
  /** vault 名称（默认"我的守狸云保险箱"）。 */
  name?: string
  /** 固定时间戳（测试用）。 */
  now?: string
  /** Argon2 参数覆盖（测试用低参数加速；默认生产强参数 64MiB/t3/p1）。 */
  argon?: { m_kib: number; t_cost: number; p_cost: number }
}

/**
 * 构造一个全新的云端 vault（uses_server_secret=true）。
 * @param pin        主密码（≥ 8 位）
 * @param sServerB64 服务端密钥分片（unlockAssist 取得）
 */
export function buildNewCloudVault(pin: string, sServerB64: string, opts: BuildOptions = {}): NewCloudVault {
  if (pin.length < 8) throw new Error('主密码至少 8 位')
  const now = opts.now ?? new Date().toISOString()
  const argon = opts.argon ?? { m_kib: 65536, t_cost: 3, p_cost: 1 } // 对齐 Rust default_argon2_params

  const kdf: KdfParams = {
    algorithm: 'argon2id',
    m_kib: argon.m_kib,
    t_cost: argon.t_cost,
    p_cost: argon.p_cost,
    salt_b64: bytesToBase64(randomBytes(16)),
  }

  // 随机 VDK，被 KEK（混入 S_server）包装。
  const vdk = randomBytes(32)
  const sServer = bytesToBase64ToBytes(sServerB64)
  const kek = deriveKek(pin, kdf, sServer)
  const pinWrapped = wrapKey(kek, vdk)

  // 恢复码（明文仅此一次返回；存其加盐指纹供校验）。
  const recoveryCode = generateRecoveryCode()
  const recoverySalt = randomBytes(16)
  const recoveryHash = recoveryCodeFingerprint(recoveryCode, recoverySalt)

  const meta = {
    id: uuid(),
    name: opts.name ?? '我的守狸云保险箱',
    kdf_params: kdf,
    created_at: now,
    schema_version: 2,
    settings: {
      auto_lock_seconds: 600,
      clipboard_clear_seconds: 30,
      require_confirm_on_reveal: true,
      reveal_window_seconds: 300,
      audit_enabled: true,
      cloud_enabled: true,
      ai_allow_category_analysis: true,
      ai_allow_risk_summary: true,
      ai_allow_advice: false,
      recovery_code_hash: recoveryHash,
      recovery_code_salt_b64: bytesToBase64(recoverySalt),
    },
    key_wrap: {
      kek_kdf: kdf,
      pin_wrapped_vdk: pinWrapped,
      // recovery_wrapped_vdk 省略（决策 D）：web 无 K_recovery，SMS 重置走桌面端补登记。
      uses_server_secret: true,
    },
  }

  const payload = {
    accounts: [],
    secrets: [],
    categories: defaultCategories(now),
    projects: defaultProjects(now),
    risks: [],
    ignored_risk_keys: [],
    recent: [],
    audit: [],
  }

  const blobB64 = bytesToBase64(packVault(meta, resealPayload(vdk, payload)))
  return { blobB64, recoveryCode }
}

function bytesToBase64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}
